import { BehaviorSubject, Observable } from 'rxjs';
import { DeliveryTrack, DeliveryTrackCourier } from '../delivery-tracking.models';
import { bearing, haversine } from '../geo';

/**
 * Simulated tracker for the no-login demo page. Everything here is sample data
 * from the shared demo cast (CONTRACT.md section 10); it never talks to a backend.
 */

export const DEMO_ORDER = {
  orderCounter: '1133',
  storeName: 'Hook Pizza Co · Hingham',
  storeAddress: 'Hingham, MA',
  timeZone: 'America/New_York',
  store: { lat: 42.2418, lng: -70.8898, phone: '7815550100' },
  destination: { lat: 42.242909, lng: -70.86715 },
  address: '55 Summer St, Apt 2',
  driver: { first_name: 'Marcus', initial: 'M' },
  proofPhoto: 'assets/delivery-demo/proof-of-delivery.svg',
};

/** The "Uber courier" scene (ENG-6000): an Uber Direct courier instead of the store's driver. */
export const DEMO_COURIER = {
  driver: { first_name: 'Alex', initial: 'A' },
  provider: 'uber',
  providerName: 'Uber',
  vehicle: 'car',
  trackingUrl: 'https://www.uber.com/track/demo-order-1133',
};

/** Road geometry store → 55 Summer St (route "store>summer55" of the demo data set, 2135 m). */
export const DEMO_ROUTE: ReadonlyArray<[number, number]> = [
  [42.24186, -70.88984],
  [42.24255, -70.8883],
  [42.24289, -70.888],
  [42.24381, -70.88561],
  [42.24518, -70.88447],
  [42.24598, -70.88189],
  [42.24584, -70.8808],
  [42.24432, -70.87732],
  [42.24363, -70.86849],
  [42.24291, -70.86715],
];
export const DEMO_ROUTE_DISTANCE_M = 2135;

export type DemoPhase = 'PREPARING' | 'READY' | 'EN_ROUTE' | 'ARRIVED' | 'DELIVERED' | 'FAILED';

export const DEMO_PHASES: ReadonlyArray<{ key: DemoPhase; label: string }> = [
  { key: 'PREPARING', label: 'Preparing' },
  { key: 'READY', label: 'Ready' },
  { key: 'EN_ROUTE', label: 'Out for delivery' },
  { key: 'ARRIVED', label: 'Arrived' },
  { key: 'DELIVERED', label: 'Delivered' },
  { key: 'FAILED', label: 'Failed' },
];

export const DEMO_SPEEDS: ReadonlyArray<number> = [1, 4, 8, 20];

/** Simulated seconds at which each phase of the happy path starts. */
const DRIVE_SECONDS = 237; // 2135 m at about 9 m/s, so the ETA starts at "about 4 minutes"
const T_READY = 45;
const T_EN_ROUTE = 75;
/** Courier scene: Uber names the courier this long after the request went out. */
const T_COURIER_NAMED = T_READY + 15;
const T_ARRIVED = T_EN_ROUTE + DRIVE_SECONDS;
const T_DELIVERED = T_ARRIVED + 30;
const PLACED_BEFORE_START_S = 15 * 60;
const TICK_MS = 250;
const POSITION_EVERY_MS = 1000;

const PHASE_START: { [phase: string]: number } = {
  PREPARING: 0,
  READY: T_READY,
  EN_ROUTE: T_EN_ROUTE,
  ARRIVED: T_ARRIVED,
  DELIVERED: T_DELIVERED,
};

export class DeliveryTrackingSimulator {
  speed = 8;
  playing = false;
  liveMap = true;
  /** When on, an Uber Direct courier carries the order instead of Marcus. */
  courier = false;

  private elapsed = 0;
  private failed = false;
  private origin = Date.now();
  private timer: any = null;
  private lastTick = 0;
  private lastPositionAt = 0;
  private lastKey = '';
  private readonly cumulative: number[];
  private readonly subject: BehaviorSubject<DeliveryTrack>;

  constructor() {
    this.cumulative = cumulativeDistances(DEMO_ROUTE);
    this.subject = new BehaviorSubject<DeliveryTrack>(this.snapshot());
  }

  get track$(): Observable<DeliveryTrack> {
    return this.subject.asObservable();
  }

  get track(): DeliveryTrack {
    return this.subject.value;
  }

  get phase(): DemoPhase {
    if (this.failed) {
      return 'FAILED';
    }
    if (this.elapsed >= T_DELIVERED) {
      return 'DELIVERED';
    }
    if (this.elapsed >= T_ARRIVED) {
      return 'ARRIVED';
    }
    if (this.elapsed >= T_EN_ROUTE) {
      return 'EN_ROUTE';
    }
    return this.elapsed >= T_READY ? 'READY' : 'PREPARING';
  }

  get finished(): boolean {
    return this.failed || this.elapsed >= T_DELIVERED;
  }

  play(): void {
    if (this.finished) {
      this.restart();
    }
    if (this.timer !== null) {
      return;
    }
    this.playing = true;
    this.lastTick = Date.now();
    this.timer = setInterval(() => {
      const now = Date.now();
      const seconds = ((now - this.lastTick) / 1000) * this.speed;
      this.lastTick = now;
      this.advance(seconds);
    }, TICK_MS);
  }

  pause(): void {
    this.playing = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart(): void {
    this.failed = false;
    this.elapsed = 0;
    this.origin = Date.now();
    this.emit(true);
  }

  jumpTo(phase: DemoPhase): void {
    if (phase === 'FAILED') {
      this.failed = true;
      // A failed drop-off happens at the door, after the drive.
      this.elapsed = Math.max(this.elapsed, T_ARRIVED);
      this.pause();
    } else {
      this.failed = false;
      this.elapsed = PHASE_START[phase];
    }
    this.emit(true);
  }

  setSpeed(speed: number): void {
    if (speed > 0 && isFinite(speed)) {
      this.speed = speed;
    }
  }

  setLiveMap(enabled: boolean): void {
    this.liveMap = enabled;
    this.emit(true);
  }

  setCourier(enabled: boolean): void {
    this.courier = enabled;
    this.emit(true);
  }

  /** Moves the simulated clock forward. Public so tests can drive it without timers. */
  advance(simSeconds: number): void {
    if (this.finished) {
      this.pause();
      return;
    }
    this.elapsed = Math.min(T_DELIVERED, this.elapsed + Math.max(0, simSeconds));
    this.emit(false);
    if (this.finished) {
      this.pause();
    }
  }

  destroy(): void {
    this.pause();
    this.subject.complete();
  }

  private emit(force: boolean): void {
    const track = this.snapshot();
    const now = Date.now();
    const courierKey = track.courier ? `${track.courier.status}|${track.driver ? 1 : 0}` : '';
    const key = `${track.state}|${track.times.ready_at ? 1 : 0}|${track.eta_minutes}|${track.live_map}|${courierKey}`;
    const positionDue = !!track.position && now - this.lastPositionAt >= POSITION_EVERY_MS;
    if (!force && key === this.lastKey && !positionDue) {
      return;
    }
    this.lastKey = key;
    if (track.position) {
      this.lastPositionAt = now;
    }
    this.subject.next(track);
  }

  private at(simSeconds: number): string {
    return new Date(this.origin + simSeconds * 1000).toISOString();
  }

  private snapshot(): DeliveryTrack {
    const phase = this.phase;
    const t = this.elapsed;
    const dispatched = t >= T_EN_ROUTE;
    const arrived = t >= T_ARRIVED;
    const moving = phase === 'EN_ROUTE' || phase === 'ARRIVED';

    const progress = Math.min(1, Math.max(0, (t - T_EN_ROUTE) / DRIVE_SECONDS));
    const remainingSeconds = Math.max(0, T_ARRIVED - t);

    let state: DeliveryTrack['state'];
    switch (phase) {
      case 'PREPARING':
        state = 'PENDING';
        break;
      case 'READY':
        state = 'ASSIGNED';
        break;
      case 'FAILED':
        state = 'FAILED';
        break;
      default:
        state = phase;
    }

    const courier = this.courier ? this.courierAt(phase, t, progress) : null;
    let driver: DeliveryTrack['driver'];
    if (this.courier) {
      // Uber names the courier once one accepts the job; nobody is named while it is still looking.
      driver = courier && t >= T_COURIER_NAMED ? { ...DEMO_COURIER.driver } : null;
    } else {
      driver = phase === 'PREPARING' ? null : { ...DEMO_ORDER.driver };
    }

    return {
      state,
      order_counter: DEMO_ORDER.orderCounter,
      store_name: DEMO_ORDER.storeName,
      store: { ...DEMO_ORDER.store },
      destination: { ...DEMO_ORDER.destination },
      driver,
      times: {
        placed_at: this.at(-PLACED_BEFORE_START_S),
        ready_at: t >= T_READY ? this.at(T_READY) : null,
        dispatched_at: dispatched ? this.at(T_EN_ROUTE) : null,
        arrived_at: arrived ? this.at(T_ARRIVED) : null,
        delivered_at: phase === 'DELIVERED' ? this.at(T_DELIVERED) : null,
      },
      eta_at: phase === 'EN_ROUTE' ? this.at(T_ARRIVED) : null,
      eta_minutes: phase === 'EN_ROUTE' ? Math.ceil(remainingSeconds / 60) : null,
      live_map: this.liveMap,
      position: moving && this.liveMap ? this.positionAt(progress) : null,
      pod:
        phase === 'DELIVERED'
          ? { type: 'photo', photo_url: DEMO_ORDER.proofPhoto, note: 'Left at the front door' }
          : null,
      courier,
    };
  }

  /** Courier payload per CONTRACT.md "Guest tracking"; null before the request and after a failure. */
  private courierAt(phase: DemoPhase, t: number, progress: number): DeliveryTrackCourier | null {
    if (phase === 'PREPARING' || phase === 'FAILED') {
      return null;
    }
    let status: string;
    let statusText: string;
    if (phase === 'READY') {
      status = t >= T_COURIER_NAMED ? 'pickup' : 'pending';
      statusText = t >= T_COURIER_NAMED ? 'Courier heading to store' : 'Finding courier';
    } else if (phase === 'DELIVERED') {
      status = 'delivered';
      statusText = 'Delivered';
    } else {
      status = progress < 0.5 ? 'pickup_complete' : 'dropoff';
      statusText = 'On the way';
    }
    return {
      provider: DEMO_COURIER.provider,
      provider_name: DEMO_COURIER.providerName,
      status,
      status_text: statusText,
      vehicle: t >= T_COURIER_NAMED ? DEMO_COURIER.vehicle : null,
      tracking_url: DEMO_COURIER.trackingUrl,
    };
  }

  private positionAt(progress: number) {
    const total = this.cumulative[this.cumulative.length - 1];
    const target = total * progress;
    let index = 1;
    while (index < this.cumulative.length - 1 && this.cumulative[index] < target) {
      index++;
    }
    const from = DEMO_ROUTE[index - 1];
    const to = DEMO_ROUTE[index];
    const span = this.cumulative[index] - this.cumulative[index - 1];
    const f = span > 0 ? Math.min(1, Math.max(0, (target - this.cumulative[index - 1]) / span)) : 1;
    return {
      // Contract: positions are rounded to 4 decimals (about 10 m).
      lat: round4(from[0] + (to[0] - from[0]) * f),
      lng: round4(from[1] + (to[1] - from[1]) * f),
      heading: Math.round(bearing({ lat: from[0], lng: from[1] }, { lat: to[0], lng: to[1] })),
      recorded_at: new Date().toISOString(),
    };
  }
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function cumulativeDistances(path: ReadonlyArray<[number, number]>): number[] {
  const out = [0];
  for (let i = 1; i < path.length; i++) {
    out.push(
      out[i - 1] + haversine({ lat: path[i - 1][0], lng: path[i - 1][1] }, { lat: path[i][0], lng: path[i][1] })
    );
  }
  return out;
}
